import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Divider,
  Input,
  Progress,
  Row,
  Space,
  Spin,
  Tabs,
  Typography,
  Upload,
  message,
  Select,
  Slider,
  Segmented,
  Tag,
  Tooltip,
} from "antd";
import {
  InboxOutlined,
  CopyOutlined,
  DownloadOutlined,
  EyeOutlined,
  FileTextOutlined,
  ReloadOutlined,
  StopOutlined,
  LikeOutlined,
  DislikeOutlined,
  LikeFilled,
  DislikeFilled,
} from "@ant-design/icons";
import AIService from "../services/AIService";
import {
  TASK_KIND,
  finishTask,
  progressTask,
  startTask,
} from "../stores/statusStore";
import { showErrorWithCopy } from "../utils/errorNotice.jsx";
import { usePasteFiles } from "../hooks/usePasteFiles";
import { emitGenerationComplete } from "../services/generationNotifications";
import {
  saveActivityPreference,
  deleteActivityPreference,
  saveActivityPreferenceAsset,
  deleteActivityPreferenceAsset,
  getSignedAssetUrl,
} from "../services/knowledgeStore";
import {
  DEFAULT_IMAGE_MODEL,
  DEFAULT_POST_COPY_MODEL,
} from "../services/modelCatalog";
import PromptEditorModal, { PromptEditorButton } from "./PromptEditorModal";
import { getSectionOverride, setSectionOverride } from "../services/sectionOverridesStore";

const { Dragger } = Upload;
const { Text, Title } = Typography;
const MAX_POST_BATCH_CONCURRENCY = 10;
const MAX_POST_ANALYSIS_CONCURRENCY = 3;
const MAX_POST_COPY_CONCURRENCY = 12;
const MAX_UPLOAD_PILLS = 2;
const POST_COPY_MODEL = DEFAULT_POST_COPY_MODEL;
const PROMPT_CHANNELS = ["Direct Response"];

// ── Analysis view ──────────────────────────────────────────────────────────
const ANALYSIS_SECTIONS = [
  { key: "visual_elements.text_overlays", label: "Headline / Text on Image" },
  { key: "messaging.value_proposition",   label: "Value Proposition" },
  { key: "messaging.cta",                 label: "CTA Text" },
  { key: "visual_elements.primary_hook",  label: "Primary Visual Hook" },
  { key: "visual_elements.composition",   label: "Composition" },
  { key: "visual_elements.key_visuals",   label: "Key Visuals" },
  { key: "emotional_response.mood",       label: "Mood / Tone" },
  { key: "target_audience.audience_segment", label: "Target Audience" },
  { key: "target_audience.psychographic_profile", label: "Psychographic Profile" },
  { key: "why_it_works",                  label: "Why It Works" },
  { key: "hook",                          label: "Hook" },
  { key: "summary",                       label: "Summary" },
];

const getNestedVal = (obj, path) => {
  const val = path.split(".").reduce((o, k) => o?.[k], obj);
  if (Array.isArray(val)) return val.filter(Boolean).join(", ");
  return String(val || "").trim();
};

const { Text: Txt } = Typography;

const AnalysisView = ({ analysis }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
    {ANALYSIS_SECTIONS.map(({ key, label }) => {
      const val = getNestedVal(analysis, key);
      if (!val) return null;
      return (
        <div key={key}>
          <Txt style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", opacity: 0.5, display: "block", marginBottom: 2 }}>
            {label}
          </Txt>
          <Txt style={{ fontSize: 13, lineHeight: 1.55 }}>{val}</Txt>
        </div>
      );
    })}
  </div>
);

// ── Prompt view — breaks the prompt into labelled sections ─────────────────
const PROMPT_SECTION_MARKERS = [
  { marker: "## IMAGE HEADLINE",     label: "Image Headline" },
  { marker: "## ANALYSIS GATE",      label: "Analysis Gate" },
  { marker: "## READING LEVEL",      label: "Reading Level Rules" },
  { marker: "## PRIMARY TEXT RULES", label: "Primary Text Rules" },
  { marker: "## HEADLINE RULES",     label: "Headline Rules" },
  { marker: "## BANNED ELEMENTS",    label: "Banned Elements" },
  { marker: "## VARIETY",            label: "Variety Rules" },
  { marker: "Analysis JSON:",        label: "Analysis JSON" },
  { marker: "Copy library",          label: "Copy Library" },
];

const PromptView = ({ prompt }) => {
  const [expanded, setExpanded] = React.useState(false);
  const lines = prompt.split("\n");

  // Try to label each paragraph based on section markers
  const sections = [];
  let current = { label: "Role & Instructions", lines: [] };
  for (const line of lines) {
    const match = PROMPT_SECTION_MARKERS.find((m) => line.trimStart().startsWith(m.marker));
    if (match) {
      if (current.lines.some((l) => l.trim())) sections.push(current);
      current = { label: match.label, lines: [line] };
    } else {
      current.lines.push(line);
    }
  }
  if (current.lines.some((l) => l.trim())) sections.push(current);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <Txt type="secondary" style={{ fontSize: 12 }}>
        Every section below was injected into the AI model before it generated copy. Edit any section via the Prompts button above.
      </Txt>
      {sections.map((sec, i) => (
        <div key={i} style={{ borderLeft: "2px solid rgba(128,128,128,0.2)", paddingLeft: 10 }}>
          <Txt style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", opacity: 0.5, display: "block", marginBottom: 4 }}>
            {sec.label}
          </Txt>
          <pre style={{ margin: 0, fontSize: 12, lineHeight: 1.55, fontFamily: "monospace", whiteSpace: "pre-wrap", wordBreak: "break-word", opacity: 0.85 }}>
            {sec.lines.join("\n").trim()}
          </pre>
        </div>
      ))}
    </div>
  );
};


const POST_TYPE_DEFAULTS = [
  {
    id: "post-type-meta",
    name: "Meta Ads",
    description: "5 headlines + 5 primary texts for ads.",
    format: "meta_ads",
  },
  {
    id: "post-type-organic",
    name: "Organic",
    description: "1-2 sentence video description + 3 hashtag bundle options.",
    format: "organic",
  },
  {
    id: "post-type-blog",
    name: "Blog Post",
    description: "Long-form blog with title, outline, body, and CTA.",
    format: "blog",
  },
  {
    id: "post-type-native",
    name: "Native Story",
    description: "Editorial-style native story with headline and CTA.",
    format: "native_story",
  },
];
const normalizePostTypeFormat = (value) => {
  const raw = String(value || "").trim().toLowerCase();
  if (
    raw === "blog" ||
    raw === "native_story" ||
    raw === "meta_ads" ||
    raw === "organic"
  ) {
    return raw;
  }
  return "meta_ads";
};

const buildPostPreferenceId = (itemId, channel, format) =>
  `post-${itemId}-${channel}-${format || "meta"}`;

const ensureLength = (arr, length) => {
  const safe = Array.isArray(arr) ? arr.filter(Boolean) : [];
  if (safe.length >= length) return safe.slice(0, length);
  return [...safe, ...Array.from({ length: length - safe.length }, () => "")];
};

const dedupeList = (arr) => {
  const seen = new Set();
  const result = [];
  for (const item of arr || []) {
    const value = String(item || "").trim();
    if (!value) continue;
    const key = value.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(value);
  }
  return result;
};

const normalizeOrganicHashtag = (value) => {
  const body = String(value || "")
    .trim()
    .replace(/^#+/, "")
    .replace(/[^A-Za-z0-9_]/g, "");
  return body ? `#${body}` : "";
};

const ORGANIC_STOP_WORDS = new Set([
  "a",
  "an",
  "and",
  "are",
  "as",
  "at",
  "be",
  "by",
  "for",
  "from",
  "getting",
  "how",
  "in",
  "into",
  "is",
  "it",
  "its",
  "man",
  "of",
  "on",
  "or",
  "shows",
  "that",
  "the",
  "their",
  "this",
  "to",
  "up",
  "video",
  "with",
]);

const toTitleHashtag = (value) => {
  const words = String(value || "")
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean)
    .filter((word) => !ORGANIC_STOP_WORDS.has(word))
    .slice(0, 2);
  if (!words.length) return "";
  const joined = words
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join("");
  return normalizeOrganicHashtag(joined);
};

const extractOrganicTags = (value) => {
  if (Array.isArray(value)) {
    return value.map(normalizeOrganicHashtag).filter(Boolean);
  }
  const raw = String(value || "").trim();
  if (!raw) return [];
  const hashtagMatches = raw.match(/#[A-Za-z0-9_]+/g);
  if (hashtagMatches?.length) {
    return hashtagMatches.map(normalizeOrganicHashtag).filter(Boolean);
  }
  return raw
    .split(/[\s,\n]+/)
    .map(normalizeOrganicHashtag)
    .filter(Boolean);
};

const normalizeOrganicBundle = (value) => {
  const tags = dedupeList(extractOrganicTags(value));
  return tags.slice(0, 6).join(" ").trim();
};

const buildOrganicFallbackBundles = (caption, existingBundles = []) => {
  const captionTags = String(caption || "")
    .split(/[.!?]/)
    .flatMap((line) => line.split(/\s+/))
    .map(toTitleHashtag)
    .filter(Boolean);
  const fallbackPool = dedupeList([
    ...captionTags,
    "#BrandStory",
    "#EverydayUse",
    "#DailyRoutine",
    "#ProductDemo",
    "#RealResults",
    "#HowItWorks",
    "#LifestyleContent",
    "#WorthTrying",
    "#CreatorPick",
    "#SimpleUpgrade",
  ]);
  const bundleSeeds = [
    [0, 1, 2, 3, 4],
    [0, 2, 5, 6, 7],
    [1, 3, 4, 8, 9],
  ];
  const safePool = fallbackPool.length >= 8
    ? fallbackPool
    : [
        ...fallbackPool,
        "#UsefulFind",
        "#ProductMoment",
        "#DailyFavorite",
        "#FreshLook",
      ];
  const normalizedExisting = dedupeList(
    (existingBundles || [])
      .map(normalizeOrganicBundle)
      .filter(Boolean),
  );
  const generated = bundleSeeds.map((indexes) =>
    indexes
      .map((index) => safePool[index % safePool.length])
      .filter(Boolean)
      .slice(0, 5)
      .join(" "),
  );
  return ensureLength(
    dedupeList([...normalizedExisting, ...generated]).filter(Boolean),
    3,
  );
};


const createLimiter = (maxActive) => {
  const state = { active: 0, queue: [] };
  return async (task) => {
    if (state.active >= maxActive) {
      await new Promise((resolve) => {
        state.queue.push(resolve);
      });
    }
    state.active += 1;
    try {
      return await task();
    } finally {
      state.active = Math.max(0, state.active - 1);
      const next = state.queue.shift();
      if (next) next();
    }
  };
};

const PostPage = ({
  apiKeys,
  analysisModel,
  aiProfile,
  analysisKnowledge,
  adCopyKnowledge,
  postTypeTags,
  postExamples,
  postCreatives,
  onPostCreativesChange,
  activityPreferences,
  onActivityPreferencesChange,
  workspaceId,
  defaultPostTypeFormat,
  pageTitle,
  pageSubtitle,
  allowedFormats,
  lockPostType = false,
  generateStoryImages = false,
}) => {
  const [messageApi, contextHolder] = message.useMessage();
  const [fileList, setFileList] = useState([]);
  const [items, setItems] = useState([]);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState({ total: 0, completed: 0 });
  const [progressNow, setProgressNow] = useState(Date.now());
  const lastProgressTickRef = useRef(Date.now());
  const lastDisplayPercentRef = useRef(0);
  const lastCompletedRef = useRef(0);
  const [search, setSearch] = useState("");
  const [copyTone, setCopyTone] = useState("Performance");
  const [creativity, setCreativity] = useState(0.7);
  const [enforceLimits, setEnforceLimits] = useState(true);
  const [dedupe, setDedupe] = useState(true);
  const [gridColumns, setGridColumns] = useState(2);
  const promptChannels = PROMPT_CHANNELS;
  const resolvedPostTypes = useMemo(() => {
    const customTypes = Array.isArray(postTypeTags) ? postTypeTags : [];
    const customFormats = new Set(
      customTypes.map((tag) => normalizePostTypeFormat(tag?.format)),
    );
    const source = [
      ...POST_TYPE_DEFAULTS.filter(
        (tag) => !customFormats.has(normalizePostTypeFormat(tag.format)),
      ).map((tag) => ({ ...tag, __builtin: true })),
      ...customTypes.map((tag) => ({
        ...tag,
        __builtin: false,
      })),
    ];
    const allowed = Array.isArray(allowedFormats)
      ? allowedFormats.map((entry) => normalizePostTypeFormat(entry))
      : [];
    const mapped = source.map((tag, index) => ({
      id: tag.id || `post-type-${index}`,
      name: tag.name || "Untitled",
      description: tag.description || "",
      format: normalizePostTypeFormat(tag.format),
      extraInfo: tag.extraInfo || "",
      __builtin: Boolean(tag.__builtin),
    }));
    const filtered = allowed.length
      ? mapped.filter((tag) => allowed.includes(tag.format))
      : mapped;
    if (filtered.length > 0) return filtered;
    if (allowed.length > 0) {
      const fallbackFormat = allowed[0];
      const fallback = POST_TYPE_DEFAULTS.find(
        (tag) => normalizePostTypeFormat(tag.format) === fallbackFormat,
      );
      if (fallback) {
        return [
          {
            id: fallback.id,
            name: fallback.name,
            description: fallback.description,
            format: normalizePostTypeFormat(fallback.format),
            __builtin: true,
          },
        ];
      }
    }
    return mapped;
  }, [postTypeTags, allowedFormats]);
  const desiredDefaultFormat = defaultPostTypeFormat
    ? normalizePostTypeFormat(defaultPostTypeFormat)
    : null;
  const [postTypeId, setPostTypeId] = useState(
    () => {
      const fallback = resolvedPostTypes[0]?.id || POST_TYPE_DEFAULTS[0].id;
      if (!desiredDefaultFormat) return fallback;
      const match = resolvedPostTypes.find(
        (tag) => tag.format === desiredDefaultFormat,
      );
      return match?.id || fallback;
    },
  );
  const [likedIds, setLikedIds] = useState(new Set());
  const [dislikedIds, setDislikedIds] = useState(new Set());
  const cancelRef = useRef(false);
  const abortRef = useRef(null);
  const pasteRef = useRef(null);

  // ── Prompt editor ─────────────────────────────────────────────────────────
  const [promptEditorOpen, setPromptEditorOpen] = useState(false);
  const [promptOverrides, setPromptOverridesState] = useState(() => getSectionOverride("adCopy"));
  const [lastPromptPreview, setLastPromptPreview] = useState("");

  const setPromptOverrides = (overrides) => {
    setPromptOverridesState(overrides);
    setSectionOverride("adCopy", overrides);
  };

  const buildUploadFile = (file) => ({
    uid: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    name: file.name || "pasted-image",
    status: "done",
    originFileObj: file,
  });

  usePasteFiles({
    ref: pasteRef,
    disabled: running,
    onFiles: (files) => {
      if (!files || files.length === 0) return;
      setFileList((prev) => [
        ...(prev || []),
        ...files.map(buildUploadFile),
      ]);
    },
  });

  useEffect(() => {
    const timer = setInterval(() => setProgressNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  const normalizedPreferences = Array.isArray(activityPreferences)
    ? activityPreferences
    : [];
  const refreshPreferenceSets = (source = normalizedPreferences) => {
    const parsed = Array.isArray(source) ? source : [];
    const likes = new Set(
      parsed
        .filter(
          (item) => item.type === "post_copy" && item.sentiment === "like",
        )
        .map((item) => item.id),
    );
    const dislikes = new Set(
      parsed
        .filter(
          (item) =>
            item.type === "post_copy" && item.sentiment === "dislike",
        )
        .map((item) => item.id),
    );
    setLikedIds(likes);
    setDislikedIds(dislikes);
  };

  useEffect(() => {
    refreshPreferenceSets();
  }, [normalizedPreferences]);

  useEffect(() => {
    if (!desiredDefaultFormat) return;
    const match = resolvedPostTypes.find(
      (tag) => tag.format === desiredDefaultFormat,
    );
    if (!match) return;
    setPostTypeId(match.id);
  }, [desiredDefaultFormat, resolvedPostTypes]);

  useEffect(() => {
    if (!resolvedPostTypes.length) return;
    const exists = resolvedPostTypes.some((tag) => tag.id === postTypeId);
    if (!exists) {
      setPostTypeId(resolvedPostTypes[0].id);
    }
  }, [resolvedPostTypes, postTypeId]);

  const filteredItems = useMemo(() => {
    const term = String(search || "").trim().toLowerCase();
    if (!term) return items;
    return items.filter((item) => {
      const format = item?.postType?.format || "meta_ads";
      const channelCopies = Object.values(item?.copyByChannel || {});
      const textPool = channelCopies.flatMap((copy) =>
        collectCopyText(copy, format),
      );
      const blob = [item?.fileName, ...textPool].join(" ").toLowerCase();
      return blob.includes(term);
    });
  }, [items, search]);

  const selectedPostType =
    resolvedPostTypes.find((tag) => tag.id === postTypeId) ||
    resolvedPostTypes[0] ||
    POST_TYPE_DEFAULTS[0];
  const headerTitle = pageTitle || "Post";
  const headerSubtitle =
    pageSubtitle ||
    "Bulk upload creatives to generate post-ready copy for Meta ads, organic social posts, blogs, or native stories.";
  const selectedPostExamples = useMemo(() => {
    const list = Array.isArray(postExamples) ? postExamples : [];
    const typeId = selectedPostType?.id || "";
    return list.filter(
      (item) => !item.postTypeId || item.postTypeId === typeId,
    );
  }, [postExamples, selectedPostType]);
  const isNativeStoryPost = selectedPostType?.format === "native_story";
  const nativeImagesEnabled = generateStoryImages || isNativeStoryPost;

  const buildNativeImagePrompt = (copy, analysis) => {
    const headline = String(copy?.headline || "").trim();
    const deck = String(copy?.deck || "").trim();
    const story = String(copy?.story || "").trim();
    const summary = String(
      analysis?.summary || analysis?.hook || analysis?.description || "",
    ).trim();
    const safeStory = story.slice(0, 450);
    return [
      headline ? `Native ad hero image for: ${headline}` : "Native ad hero image",
      deck,
      summary,
      safeStory,
      "Style: editorial, premium lifestyle photography, natural lighting, authentic candid moment, no text overlay, no logos, no watermark.",
    ]
      .filter(Boolean)
      .join("\n");
  };


  const canRun = fileList.length > 0 && !running;
  const visibleUploadPills = fileList.slice(0, MAX_UPLOAD_PILLS);
  const hiddenUploadCount = Math.max(
    0,
    fileList.length - visibleUploadPills.length,
  );
  const smoothPercent = (() => {
    const total = progress.total || 0;
    const completed = progress.completed || 0;
    if (total <= 0) {
      lastDisplayPercentRef.current = 0;
      return 0;
    }
    if (completed !== lastCompletedRef.current) {
      lastCompletedRef.current = completed;
      lastProgressTickRef.current = progressNow;
    }
    let nextPercent = Math.max(
      lastDisplayPercentRef.current,
      Math.round((completed / Math.max(1, total)) * 100),
    );
    if (running) {
      const elapsedMs = progressNow - lastProgressTickRef.current;
      if (elapsedMs >= 3000) {
        nextPercent = Math.min(99, nextPercent + 1);
        lastProgressTickRef.current = progressNow;
      }
    }
    if (!running) {
      nextPercent = 100;
    }
    if (completed >= total) {
      nextPercent = 100;
    }
    lastDisplayPercentRef.current = nextPercent;
    return nextPercent;
  })();

  const handleCopy = async (value) => {
    try {
      await navigator.clipboard.writeText(String(value || "").trim());
      messageApi.success("Copied");
    } catch (error) {
      messageApi.error("Copy failed");
    }
  };

  const getItemExportSnapshot = (item) => {
    const format = normalizePostTypeFormat(item?.postType?.format);
    const copy = normalizePostOutput(
      format,
      item?.copyByChannel?.[promptChannels[0]] || item?.copy || {},
    );
    return { format, copy };
  };

  const resolveExportMediaUrl = async (item) => {
    const directCandidates = [
      item?.file?.sourceUrl,
      item?.file?.url,
      item?.file?.thumbUrl,
      item?.mediaUrl,
      item?.previewUrl,
    ]
      .map((value) => String(value || "").trim())
      .filter(Boolean);
    const stableUrl = directCandidates.find((value) => !value.startsWith("blob:"));
    if (stableUrl) return stableUrl;
    if (item?.file?.path) {
      try {
        const signed = await getSignedAssetUrl(item.file.path);
        if (signed?.url) return String(signed.url).trim();
      } catch (error) {
        // Fall back to whatever preview URL we already have.
      }
    }
    return directCandidates[0] || "";
  };

  const sanitizeCsvValue = (value) => {
    const normalized = String(value ?? "")
      .replace(/\r\n/g, "\n")
      .replace(/\r/g, "\n");
    return /^[=+\-@\t]/.test(normalized) ? `'${normalized}` : normalized;
  };

  const escapeCsvCell = (value) =>
    `"${sanitizeCsvValue(value).replace(/"/g, '""')}"`;

  const buildExportRow = async (item) => {
    const { format, copy } = getItemExportSnapshot(item);
    const primaryTexts = Array.from({ length: 5 }, () => "");
    const headlines = Array.from({ length: 5 }, () => "");

    if (format === "meta_ads") {
      ensureLength(copy?.primaryTexts, 5).forEach((value, index) => {
        primaryTexts[index] = value;
      });
      ensureLength(copy?.headlines, 5).forEach((value, index) => {
        headlines[index] = value;
      });
    } else if (format === "native_story") {
      headlines[0] = copy?.headline || "";
      primaryTexts[0] = copy?.deck || "";
      primaryTexts[1] = copy?.story || "";
      primaryTexts[2] = copy?.cta || "";
    } else if (format === "organic") {
      primaryTexts[0] = copy?.caption || "";
      ensureLength(copy?.hashtagBundles, 3).forEach((value, index) => {
        primaryTexts[index + 1] = value;
      });
    } else if (format === "blog") {
      headlines[0] = copy?.title || "";
      primaryTexts[0] = copy?.subtitle || "";
      primaryTexts[1] = (copy?.outline || []).filter(Boolean).join(" | ");
      primaryTexts[2] = copy?.body || "";
      primaryTexts[3] = copy?.cta || "";
    }

    return {
      creative_name: item?.fileName || "Creative",
      post_type: format,
      media_type: resolvePreferenceMediaType(item),
      creative_preview_url: await resolveExportMediaUrl(item),
      primary_text_1: primaryTexts[0] || "",
      primary_text_2: primaryTexts[1] || "",
      primary_text_3: primaryTexts[2] || "",
      primary_text_4: primaryTexts[3] || "",
      primary_text_5: primaryTexts[4] || "",
      headline_1: headlines[0] || "",
      headline_2: headlines[1] || "",
      headline_3: headlines[2] || "",
      headline_4: headlines[3] || "",
      headline_5: headlines[4] || "",
    };
  };

  const handleExportCsv = async () => {
    const exportableItems = (items || []).filter((item) => {
      const { format, copy } = getItemExportSnapshot(item);
      return collectCopyText(copy, format).length > 0;
    });
    if (!exportableItems.length) {
      messageApi.info("Generate some post copy first.");
      return;
    }
    try {
      const rows = await Promise.all(exportableItems.map(buildExportRow));
      const columns = [
        "creative_name",
        "post_type",
        "media_type",
        "creative_preview_url",
        "primary_text_1",
        "primary_text_2",
        "primary_text_3",
        "primary_text_4",
        "primary_text_5",
        "headline_1",
        "headline_2",
        "headline_3",
        "headline_4",
        "headline_5",
      ];
      const csv = [
        columns.join(","),
        ...rows.map((row) =>
          columns.map((column) => escapeCsvCell(row[column] || "")).join(","),
        ),
      ].join("\n");
      const blob = new Blob([`\ufeff${csv}`], {
        type: "text/csv;charset=utf-8;",
      });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `post-export-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
      messageApi.success(`Exported ${rows.length} creative${rows.length === 1 ? "" : "s"} to CSV`);
    } catch (error) {
      messageApi.error(error?.message || "Failed to export CSV");
    }
  };

  const fetchBlobWithRetry = async (url, maxAttempts = 3, signal) => {
    const target = String(url || "").trim();
    if (!target) return null;
    let lastError = null;
    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
      try {
        const response = await fetch(target, { signal });
        if (!response.ok) {
          throw new Error(`Failed to load creative (${response.status})`);
        }
        return await response.blob();
      } catch (error) {
        if (signal?.aborted) {
          throw error;
        }
        lastError = error;
        if (attempt < maxAttempts) {
          await new Promise((resolve) => setTimeout(resolve, 300 * attempt));
        }
      }
    }
    throw lastError || new Error("Failed to load creative");
  };

  const resolvePreferenceMediaType = (item) =>
    item.mediaType ||
    (String(item?.file?.type || "").startsWith("video") ? "video" : "image");

  const resolvePreferenceBlob = async (item) => {
    const directFile = item?.file?.originFileObj;
    if (directFile instanceof Blob) {
      return directFile;
    }
    if (item?.file instanceof Blob) {
      return item.file;
    }
    const sourceUrl =
      item.previewUrl ||
      item.mediaUrl ||
      item?.file?.sourceUrl ||
      item?.file?.url ||
      item?.file?.thumbUrl ||
      "";
    return fetchBlobWithRetry(sourceUrl);
  };

  const persistPreferenceMedia = async ({ safeId, item }) => {
    const mediaType = resolvePreferenceMediaType(item);
    const blob = await resolvePreferenceBlob(item);
    if (!blob) {
      throw new Error("Missing creative media for preference save");
    }
    const fallbackMimeType = mediaType === "video" ? "video/mp4" : "image/png";
    const mimeType = blob.type || fallbackMimeType;
    const extension = mediaType === "video" ? "mp4" : "png";
    const fileLabel = String(item.fileName || "creative").trim() || "creative";
    const stored = await saveActivityPreferenceAsset({
      id: safeId,
      name: `${fileLabel}.${extension}`,
      blob,
      mimeType,
    });
    if (!stored?.path) {
      throw new Error("Upload completed but no storage path was returned.");
    }
    return {
      mediaUrl: stored?.url || "",
      path: stored.path,
      signedAt: stored?.signedAt || new Date().toISOString(),
      mimeType: stored?.mimeType || mimeType,
      mediaType,
    };
  };

  const savePostPreference = async (item, channel, sentiment) => {
    const safeId = `post-${item.id}-${channel}-${item.postType?.format || "meta"}`;
    try {
      const already = normalizedPreferences.find((entry) => entry.id === safeId);
      if (already && already.sentiment === sentiment) {
        await deleteActivityPreference(safeId).catch(() => {});
        if (already?.path) {
          await deleteActivityPreferenceAsset(safeId).catch(() => {});
        }
        const next = normalizedPreferences.filter((entry) => entry.id !== safeId);
        onActivityPreferencesChange?.(next);
        refreshPreferenceSets(next);
        messageApi.success("Preference removed");
        return;
      }
      const media = await persistPreferenceMedia({ safeId, item });
      const record = {
        id: safeId,
        type: "post_copy",
        createdAt: new Date().toISOString(),
        sentiment,
        channel,
        postType: item.postType || null,
        copy: item.copyByChannel?.[channel] || {},
        analysis: item.analysis || null,
        mediaUrl: media.mediaUrl,
        path: media.path,
        signedAt: media.signedAt,
        mimeType: media.mimeType,
        mediaType: media.mediaType,
        fileName: item.fileName || "",
      };
      await saveActivityPreference(record);
      const next = [
        record,
        ...normalizedPreferences.filter((entry) => entry.id !== safeId),
      ];
      onActivityPreferencesChange?.(next);
      refreshPreferenceSets(next);
      messageApi.success(sentiment === "like" ? "Saved as liked" : "Saved as disliked");
    } catch (error) {
      const detail = error?.message ? `: ${error.message}` : "";
      messageApi.error(`Failed to save preference${detail}`);
    }
  };

  const renderCopyPill = (text, key, className = "") => (
    <button
      key={key}
      type="button"
      className={`post-copy-pill ${className}`.trim()}
      onClick={() => handleCopy(text)}
    >
      <CopyOutlined />
      <span className="post-copy-pill__text">{text || "-"}</span>
    </button>
  );

  const renderCopyContent = (copy, format, keyPrefix) => {
    const safeFormat = normalizePostTypeFormat(format);
    if (safeFormat === "blog") {
      return (
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <div>
            <Text strong>Title</Text>
            <div className="post-copy-grid">
              {renderCopyPill(copy?.title, `${keyPrefix}-title`)}
            </div>
          </div>
          <div>
            <Text strong>Subtitle</Text>
            <div className="post-copy-grid">
              {renderCopyPill(copy?.subtitle, `${keyPrefix}-subtitle`)}
            </div>
          </div>
          <div>
            <Text strong>Outline</Text>
            <div className="post-copy-grid">
              {(copy?.outline || []).map((text, idx) =>
                renderCopyPill(text, `${keyPrefix}-outline-${idx}`),
              )}
            </div>
          </div>
          <div>
            <Text strong>Body</Text>
            <div className="post-copy-grid">
              {renderCopyPill(copy?.body, `${keyPrefix}-body`)}
            </div>
          </div>
          <div>
            <Text strong>CTA</Text>
            <div className="post-copy-grid">
              {renderCopyPill(copy?.cta, `${keyPrefix}-cta`)}
            </div>
          </div>
        </Space>
      );
    }
    if (safeFormat === "native_story") {
      return (
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <div>
            <Text strong>Headline</Text>
            <div className="post-copy-grid">
              {renderCopyPill(copy?.headline, `${keyPrefix}-headline`)}
            </div>
          </div>
          <div>
            <Text strong>Deck</Text>
            <div className="post-copy-grid">
              {renderCopyPill(copy?.deck, `${keyPrefix}-deck`)}
            </div>
          </div>
          <div>
            <Text strong>Story</Text>
            <div className="post-copy-grid">
              {renderCopyPill(copy?.story, `${keyPrefix}-story`)}
            </div>
          </div>
          <div>
            <Text strong>CTA</Text>
            <div className="post-copy-grid">
              {renderCopyPill(copy?.cta, `${keyPrefix}-cta`)}
            </div>
          </div>
        </Space>
      );
    }
    if (safeFormat === "organic") {
      return (
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <div>
            <Text strong>Video Description</Text>
            <div className="post-copy-grid post-copy-grid--organic">
              {renderCopyPill(
                copy?.caption,
                `${keyPrefix}-caption`,
                "post-copy-pill--wide",
              )}
            </div>
          </div>
          <div>
            <Text strong>Hashtag Bundles</Text>
            <div className="post-copy-grid post-copy-grid--organic">
              {(copy?.hashtagBundles || []).map((text, idx) =>
                renderCopyPill(
                  text,
                  `${keyPrefix}-hashtag-bundle-${idx}`,
                  "post-copy-pill--wide post-copy-pill--hashtags",
                ),
              )}
            </div>
          </div>
        </Space>
      );
    }
    return (
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        <div>
          <Text strong>Primary Text</Text>
          <div className="post-copy-grid">
            {ensureLength(copy?.primaryTexts, 5).map((text, idx) =>
              renderCopyPill(text, `${keyPrefix}-p-${idx}`),
            )}
          </div>
        </div>
        <div>
          <Text strong>Headlines</Text>
          <div className="post-copy-grid">
            {ensureLength(copy?.headlines, 5).map((text, idx) =>
              renderCopyPill(text, `${keyPrefix}-h-${idx}`),
            )}
          </div>
        </div>
      </Space>
    );
  };

  const handleAddSavedCreative = (saved) => {
    const url = saved?.url || saved?.mediaUrl || saved?.previewUrl || "";
    if (!url) {
      messageApi.error("Saved creative missing URL");
      return;
    }
    const exists = fileList.some((item) => item.savedId === saved.id);
    if (exists) {
      messageApi.info("Already added to the batch");
      return;
    }
    const mimeType =
      saved?.mimeType ||
      (saved?.mediaType === "video" ? "video/mp4" : "image/png");
    const nextItem = {
      uid: `saved-${saved.id}-${Date.now()}`,
      name: saved?.name || "Saved creative",
      status: "done",
      url,
      thumbUrl: url,
      type: mimeType,
      originFileObj: null,
      sourceUrl: url,
      path: saved?.path || "",
      mediaType: saved?.mediaType || "",
      savedId: saved?.id || "",
    };
    setFileList((prev) => [nextItem, ...prev]);
    messageApi.success("Added saved creative");
  };

  const normalizeMetaCopy = (payload) => {
    const collectList = (...values) => {
      const merged = values
        .flatMap((value) => (Array.isArray(value) ? value : value ? [value] : []))
        .map((item) => String(item || "").trim())
        .filter(Boolean);
      return dedupe ? dedupeList(merged) : merged;
    };
    const cleanNoEllipsis = (text) =>
      String(text || "")
        .replace(/\.{3,}|…/g, " ")
        .replace(/\s+/g, " ")
        .trim();
    const countWords = (text) =>
      String(text || "")
        .trim()
        .split(/\s+/)
        .filter(Boolean).length;
    const sentenceCount = (text) =>
      String(text || "")
        .split(/(?<=[.!?])\s+/)
        .map((item) => item.trim())
        .filter(Boolean).length;
    const hasTooManyLongWords = (text, maxLen = 10, limit = 1) => {
      const count = String(text || "")
        .split(/\s+/)
        .map((word) => word.replace(/[^a-z]/gi, ""))
        .filter(Boolean)
        .filter((word) => word.length > maxLen).length;
      return count > limit;
    };
    const looksLikeAnalysisLine = (text, type = "primary") => {
      const value = String(text || "").trim();
      if (!value) return true;
      const lower = value.toLowerCase();
      const blocked = [
        "this ad",
        "the ad",
        "this creative",
        "the creative",
        "visual",
        "image",
        "photo",
        "camera",
        "lighting",
        "composition",
        "the clip",
        "clip starts",
        "scene",
        "actor",
        "creator",
        "dialogue",
        "voiceover",
        "on-screen",
        "stitch",
        "shot",
        "segment",
        "timing",
        "continuity",
        "analysis",
        "why it works",
      ];
      if (blocked.some((token) => lower.includes(token))) return true;
      if (/^\s*[{\[]/.test(value)) return true;
      if (/"[^"]+"\s*:/.test(value) || /'[^']+'\s*:/.test(value)) return true;
      if (/^(headline|primary text|body copy|cta)\s*:/i.test(value)) return true;
      const words = countWords(value);
      if (type === "headline" && (words < 2 || words > 6)) return true;
      if (type === "primary" && (words < 8 || words > 30)) return true;
      if (type === "primary" && sentenceCount(value) > 2) return true;
      if (type === "headline" && hasTooManyLongWords(value, 10, 0)) return true;
      if (type === "primary" && hasTooManyLongWords(value, 11, 1)) return true;
      return false;
    };
    const source = payload && typeof payload === "object" ? payload : {};
    const nested =
      source?.copy && typeof source.copy === "object" ? source.copy : {};
    const rawHeadlines = collectList(
      source?.headlines,
      source?.headlineOptions,
      source?.headline_options,
      nested?.headlines,
      nested?.headlineOptions,
      nested?.headline_options,
    )
      .map(cleanNoEllipsis)
      .filter((line) => !looksLikeAnalysisLine(line, "headline"));
    const rawPrimary = collectList(
      source?.primaryTexts,
      source?.primary_texts,
      source?.primaryOptions,
      source?.primary_options,
      source?.body,
      source?.bodyOptions,
      source?.body_options,
      nested?.primaryTexts,
      nested?.primary_texts,
      nested?.primaryOptions,
      nested?.primary_options,
      nested?.body,
      nested?.bodyOptions,
      nested?.body_options,
    )
      .map(cleanNoEllipsis)
      .filter((line) => !looksLikeAnalysisLine(line, "primary"));
    return {
      headlines: ensureLength(rawHeadlines.slice(0, 5), 5),
      primaryTexts: ensureLength(rawPrimary.slice(0, 5), 5),
    };
  };

  const normalizePostOutput = (format, payload) => {
    const safeFormat = normalizePostTypeFormat(format);
    if (safeFormat === "blog") {
      const outlineRaw = Array.isArray(payload?.outline)
        ? payload.outline
        : Array.isArray(payload?.bullets)
          ? payload.bullets
          : Array.isArray(payload?.sections)
            ? payload.sections
            : [];
      const outlineClean = outlineRaw.map((item) => String(item || "").trim());
      const outline = ensureLength(
        dedupe ? dedupeList(outlineClean) : outlineClean.filter(Boolean),
        4,
      );
      return {
        title: String(payload?.title || payload?.headline || "").trim(),
        subtitle: String(payload?.subtitle || payload?.deck || "").trim(),
        outline,
        body: String(payload?.body || payload?.story || "").trim(),
        cta: String(payload?.cta || payload?.callToAction || "").trim(),
      };
    }
    if (safeFormat === "native_story") {
      return {
        headline: String(payload?.headline || payload?.title || "").trim(),
        deck: String(payload?.deck || payload?.subtitle || "").trim(),
        story: String(payload?.story || payload?.body || "").trim(),
        cta: String(payload?.cta || payload?.callToAction || "").trim(),
      };
    }
    if (safeFormat === "organic") {
      const caption = String(
        payload?.caption ||
          payload?.description ||
          payload?.sentence ||
          payload?.body ||
          "",
      )
        .replace(/\s+/g, " ")
        .trim();
      const rawBundleSource = Array.isArray(payload?.hashtagBundles)
        ? payload.hashtagBundles
        : Array.isArray(payload?.hashtag_sets)
          ? payload.hashtag_sets
          : Array.isArray(payload?.hashtagSets)
            ? payload.hashtagSets
            : [];
      const fallbackSingleBundle = normalizeOrganicBundle(
        Array.isArray(payload?.hashtags)
          ? payload.hashtags
          : Array.isArray(payload?.tags)
            ? payload.tags
            : String(payload?.hashtags || payload?.tags || ""),
      );
      const normalizedBundles = dedupeList(
        rawBundleSource
          .map(normalizeOrganicBundle)
          .filter(Boolean)
          .concat(fallbackSingleBundle ? [fallbackSingleBundle] : []),
      );
      return {
        caption,
        hashtagBundles: buildOrganicFallbackBundles(caption, normalizedBundles),
      };
    }
    return normalizeMetaCopy(payload);
  };

  const collectCopyText = (copy, format) => {
    if (!copy) return [];
    const safeFormat = normalizePostTypeFormat(format);
    if (safeFormat === "blog") {
      return [
        copy.title,
        copy.subtitle,
        ...(copy.outline || []),
        copy.body,
        copy.cta,
      ].filter(Boolean);
    }
    if (safeFormat === "native_story") {
      return [copy.headline, copy.deck, copy.story, copy.cta].filter(Boolean);
    }
    if (safeFormat === "organic") {
      return [copy.caption, ...(copy.hashtagBundles || [])].filter(Boolean);
    }
    return [
      ...(copy.headlines || []),
      ...(copy.primaryTexts || []),
    ].filter(Boolean);
  };

  const handleOpenPromptEditor = async () => {
    const firstItem = items.find((item) => item.analysis);
    const analysis = firstItem?.analysis || null;
    if (analysis) {
      try {
        const aiService = new AIService({ workspaceId });
        const result = await aiService.generateAdCopyFromAnalysis(analysis, {
          channel: (promptChannels || [])[0] || "Direct Response",
          tone: copyTone,
          creativity,
          enforceLimits,
          postType: selectedPostType,
          postExamples: selectedPostExamples,
          _previewOnly: true,
        });
        setLastPromptPreview(result?.prompt || "");
      } catch {
        setLastPromptPreview("");
      }
    }
    setPromptEditorOpen(true);
  };

  const handleStart = async () => {
    if (!fileList.length) {
      messageApi.info("Add creatives to analyze.");
      return;
    }
    cancelRef.current = false;
    abortRef.current = new AbortController();
    const controller = abortRef.current;
    const { signal } = controller;
    lastDisplayPercentRef.current = 0;
    lastCompletedRef.current = 0;
    lastProgressTickRef.current = Date.now();
    const isCancelled = () => cancelRef.current || signal.aborted;
    setRunning(true);
    setProgress({ total: fileList.length, completed: 0 });
    let completedCount = 0;
    let failedCount = 0;
    let cancelled = false;

    const baseItems = fileList.map((file, index) => {
      const rawFile = file.originFileObj || file;
      const previewUrl =
        file.thumbUrl ||
        file.url ||
        file.sourceUrl ||
        (rawFile instanceof Blob ? URL.createObjectURL(rawFile) : "");
      return {
        id: `${file.uid || "creative"}-${index}`,
        file,
        fileName: file.name,
        previewUrl,
        status: "queued",
        analysis: null,
        copy: null,
        copyByChannel: {},
        postType: selectedPostType,
        error: "",
      };
    });
    setItems(baseItems);

    const batchTaskId = `analysis-batch:${Date.now()}`;
    startTask(batchTaskId, {
      kind: TASK_KIND.ANALYSIS_BATCH,
      label: `Posts · ${baseItems.length} creative${baseItems.length === 1 ? "" : "s"}`,
      total: baseItems.length,
    });

    const aiService = new AIService({
      ...apiKeys,
      aiProfile,
      analysisKnowledge,
      adCopyKnowledge,
      activityPreferences,
      workspaceId,
    });

    let hardStopMessage = "";
    let hardStopNotified = false;
    const withAnalysisSlot = createLimiter(MAX_POST_ANALYSIS_CONCURRENCY);
    const withCopySlot = createLimiter(MAX_POST_COPY_CONCURRENCY);

    const isAbortError = (error) => {
      return (
        String(error?.message || "").toLowerCase().includes("cancel") ||
        String(error?.name || "").toLowerCase() === "aborterror" ||
        error?.code === 20
      );
    };

    const markActiveOrQueuedCancelled = (reason = "Cancelled") => {
      setItems((prev) =>
        prev.map((item) =>
          item.status === "queued" || item.status === "analyzing"
            ? { ...item, status: "cancelled", error: reason }
            : item,
        ),
      );
    };

    const processEntry = async (entry) => {
      if (isCancelled()) {
        cancelled = true;
        return;
      }
      try {
        let file = entry.file.originFileObj || entry.file;
        const sourceUrl =
          entry.file.sourceUrl || entry.file.url || entry.file.thumbUrl || "";
        if (!(file instanceof Blob) && sourceUrl) {
          let blob = null;
          try {
            blob = await fetchBlobWithRetry(sourceUrl, 3, signal);
          } catch (error) {
            if (!entry.file?.path) {
              throw error;
            }
            const refreshed = await getSignedAssetUrl(entry.file.path);
            if (!refreshed?.url) {
              throw error;
            }
            blob = await fetchBlobWithRetry(refreshed.url, 3, signal);
          }
          file = new File([blob], entry.fileName || "saved-creative", {
            type: blob.type || entry.file.type || "",
          });
        }
        if (!(file instanceof Blob)) {
          throw new Error("Missing file data for analysis");
        }
        const isVideo =
          String(file?.type || "").startsWith("video") ||
          entry.file?.mediaType === "video";
        setItems((prev) =>
          prev.map((item) =>
            item.id === entry.id ? { ...item, status: "analyzing" } : item,
          ),
        );
        const analysis = await withAnalysisSlot(() =>
          aiService.analyzeCreative(
            file,
            isVideo,
            null,
            signal,
            { model: analysisModel },
          ),
        );
        setItems((prev) =>
          prev.map((item) =>
            item.id === entry.id ? { ...item, analysis } : item,
          ),
        );
        if (isCancelled()) {
          cancelled = true;
          setItems((prev) =>
            prev.map((item) =>
              item.id === entry.id
                ? {
                    ...item,
                    status: "cancelled",
                    error: hardStopMessage || "Cancelled",
                  }
                : item,
            ),
          );
          return;
        }
        // Capture the built prompt for display in the Prompt tab
        let capturedPrompt = "";
        try {
          const previewResult = await aiService.generateAdCopyFromAnalysis(analysis, {
            modelName: POST_COPY_MODEL,
            channel: (promptChannels || [])[0] || "Direct Response",
            tone: copyTone,
            creativity,
            enforceLimits,
            postType: selectedPostType,
            postExamples: selectedPostExamples,
            promptOverride: promptOverrides.main || "",
            _previewOnly: true,
          });
          capturedPrompt = previewResult?.prompt || "";
        } catch { /* non-fatal */ }
        setItems((prev) =>
          prev.map((item) =>
            item.id === entry.id ? { ...item, promptUsed: capturedPrompt } : item,
          ),
        );

        const channelOutputs = {};
        const channelErrors = [];
        await Promise.all(
          (promptChannels || []).map(async (channel) => {
            if (isCancelled()) return;
            try {
              const copy = await withCopySlot(() =>
                aiService.generateAdCopyFromAnalysis(analysis, {
                  modelName: POST_COPY_MODEL,
                  channel,
                  tone: copyTone,
                  creativity,
                  enforceLimits,
                  postType: selectedPostType,
                  postExamples: selectedPostExamples,
                  signal,
                  promptOverride: promptOverrides.main || "",
                }),
              );
              channelOutputs[channel] = normalizePostOutput(
                selectedPostType?.format,
                copy,
              );
            } catch (channelError) {
              channelErrors.push(
                `${channel}: ${channelError?.message || "Generation failed"}`,
              );
              channelOutputs[channel] = normalizePostOutput(
                selectedPostType?.format,
                {},
              );
            }
          }),
        );
        if (channelErrors.length === promptChannels.length) {
          throw new Error(
            channelErrors[0] || "All channels failed to generate copy",
          );
        }
        if (isCancelled()) {
          cancelled = true;
          setItems((prev) =>
            prev.map((item) =>
              item.id === entry.id
                ? {
                    ...item,
                    status: "cancelled",
                    error: hardStopMessage || "Cancelled",
                  }
                : item,
            ),
          );
          return;
        }
        const fallbackCopy =
          channelOutputs[promptChannels[0]] ||
          normalizePostOutput(selectedPostType?.format, {});

        let nativeImageUrl = "";
        let nativeImageError = "";
        if (
          nativeImagesEnabled &&
          isNativeStoryPost &&
          !isCancelled()
        ) {
          try {
            nativeImageUrl = await aiService.generateBlogImage({
              prompt: buildNativeImagePrompt(fallbackCopy, analysis),
              aspectRatio: "4:3",
              imageModel: DEFAULT_IMAGE_MODEL,
            });
          } catch (imageError) {
            nativeImageError =
              imageError?.message || "Failed to generate native companion image";
          }
        }

        setItems((prev) =>
          prev.map((item) =>
            item.id === entry.id
              ? {
                    ...item,
                    status: "done",
                    copy: fallbackCopy,
                    copyByChannel: channelOutputs,
                    postType: selectedPostType,
                    nativeImageUrl,
                    nativeImageError,
                    error:
                      channelErrors.length > 0
                        ? `${channelErrors.length} channel${channelErrors.length === 1 ? "" : "s"} failed`
                        : "",
                }
              : item,
          ),
        );
        completedCount += 1;
      } catch (error) {
        const isCancellation =
          isCancelled() ||
          isAbortError(error) ||
          String(error?.message || "").toLowerCase().includes("cancel");
        if (isCancellation) {
          cancelled = true;
          setItems((prev) =>
            prev.map((item) =>
              item.id === entry.id
                ? {
                    ...item,
                    status: "cancelled",
                    error: hardStopMessage || "Cancelled",
                  }
                : item,
            ),
          );
        } else {
          failedCount += 1;
          setItems((prev) =>
            prev.map((item) =>
              item.id === entry.id
                ? {
                    ...item,
                    status: "error",
                    error: error?.message || "Failed",
                  }
                : item,
            ),
          );
          showErrorWithCopy(
            messageApi,
            "Post analysis failed",
            error,
            "PostPage.handleStart",
          );
        }
      } finally {
        setProgress((prev) => {
          const next = Math.min(prev.total, prev.completed + 1);
          progressTask(batchTaskId, next);
          return { ...prev, completed: next };
        });
      }
    };

    const workerCount = Math.max(
      1,
      Math.min(MAX_POST_BATCH_CONCURRENCY, baseItems.length),
    );
    let cursor = 0;
    const workers = Array.from({ length: workerCount }, async () => {
      while (true) {
        if (isCancelled()) {
          cancelled = true;
          return;
        }
        const nextEntry = baseItems[cursor];
        cursor += 1;
        if (!nextEntry) {
          return;
        }
        await processEntry(nextEntry);
      }
    });

    try {
      await Promise.all(workers);

      if (cancelRef.current) {
        cancelled = true;
        markActiveOrQueuedCancelled(hardStopMessage || "Cancelled");
      }

      const processedCount = completedCount + failedCount;
      if (processedCount > 0 && !cancelled) {
        const hasFailures = failedCount > 0;
        emitGenerationComplete({
          source: "post",
          title: hasFailures ? "Post generation finished with issues" : "Post generation complete",
          body: hasFailures
            ? `${completedCount} completed, ${failedCount} failed.`
            : `${completedCount} post${completedCount === 1 ? "" : "s"} ready.`,
          success: !hasFailures,
        });
      }
    } finally {
      if (isCancelled()) {
        markActiveOrQueuedCancelled(hardStopMessage || "Cancelled");
      }
      if (abortRef.current === controller) {
        abortRef.current = null;
      }
      const finishStatus = failedCount > 0 ? "error" : "done";
      const finishDetails =
        failedCount > 0
          ? {
              error: `${failedCount} of ${baseItems.length} failed`,
            }
          : {};
      finishTask(batchTaskId, finishStatus, finishDetails);
      setRunning(false);
    }
  };

  const handleReset = () => {
    setFileList([]);
    setItems([]);
    setProgress({ total: 0, completed: 0 });
    lastDisplayPercentRef.current = 0;
    lastCompletedRef.current = 0;
    lastProgressTickRef.current = Date.now();
    setSearch("");
  };

  const handleCancel = () => {
    cancelRef.current = true;
    abortRef.current?.abort();
    setItems((prev) =>
      prev.map((item) =>
        ["queued", "analyzing"].includes(item.status)
          ? { ...item, status: "cancelled", error: "Cancelled" }
          : item,
      ),
    );
  };

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      {contextHolder}
      <div>
        <Title level={3} style={{ marginBottom: 4 }}>
          {headerTitle}
        </Title>
        <Text type="secondary">{headerSubtitle}</Text>
      </div>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={14}>
          <Card className="panel-card">
            <Space direction="vertical" size="middle" style={{ width: "100%" }}>
              <div ref={pasteRef} tabIndex={0} className="paste-upload-area">
                <Dragger
                  multiple
                  accept="image/*,video/*"
                  beforeUpload={() => false}
                  showUploadList={false}
                  fileList={fileList}
                  onChange={({ fileList: next }) => setFileList(next)}
                >
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                  </p>
                  <p className="ant-upload-text">
                    Drag & drop creatives here, or click to select files.
                  </p>
                  <p className="ant-upload-hint">
                    Supports images and videos. Analysis runs per creative.
                  </p>
                </Dragger>
              </div>
              {fileList.length > 0 ? (
                <div className="post-upload-pills">
                  {visibleUploadPills.map((file) => (
                    <div key={file.uid} className="post-upload-pill">
                      <span
                        className="post-upload-pill__name"
                        title={file.name || "Creative"}
                      >
                        {file.name || "Creative"}
                      </span>
                    </div>
                  ))}
                  {hiddenUploadCount > 0 ? (
                    <div className="post-upload-pill post-upload-pill--more">
                      +{hiddenUploadCount}
                    </div>
                  ) : null}
                </div>
              ) : null}

              {Array.isArray(postCreatives) && postCreatives.length > 0 ? (
                <Space direction="vertical" size="small" style={{ width: "100%" }}>
                  <Text type="secondary">Saved creatives</Text>
                  <Space wrap>
                    {postCreatives.slice(0, 12).map((saved) => {
                      const url = saved?.url || saved?.mediaUrl || "";
                      const isVideo =
                        saved?.mediaType === "video" ||
                        String(saved?.mimeType || "").startsWith("video");
                      return (
                        <div
                          key={saved.id}
                          style={{
                            border: "1px solid rgba(15, 23, 42, 0.08)",
                            borderRadius: 10,
                            padding: 6,
                            width: 120,
                            background: "rgba(255,255,255,0.6)",
                          }}
                        >
                          {url ? (
                            isVideo ? (
                              <video
                                src={url}
                                style={{
                                  width: "100%",
                                  height: 72,
                                  objectFit: "cover",
                                  borderRadius: 8,
                                  background: "#111",
                                }}
                              />
                            ) : (
                              <img
                                src={url}
                                alt={saved.name || "Saved creative"}
                                style={{
                                  width: "100%",
                                  height: 72,
                                  objectFit: "cover",
                                  borderRadius: 8,
                                  background: "#111",
                                }}
                              />
                            )
                          ) : (
                            <div
                              style={{
                                width: "100%",
                                height: 72,
                                borderRadius: 8,
                                background: "#111",
                              }}
                            />
                          )}
                          <Button
                            size="small"
                            style={{ marginTop: 6 }}
                            block
                            onClick={() => handleAddSavedCreative(saved)}
                          >
                            Add
                          </Button>
                        </div>
                      );
                    })}
                  </Space>
                </Space>
              ) : null}

              <Space wrap>
                <Button type="primary" onClick={handleStart} disabled={!canRun}>
                  Analyze & Generate Copy
                </Button>
                <PromptEditorButton
                  onClick={handleOpenPromptEditor}
                  hasOverride={Boolean(promptOverrides.main)}
                />
                <Button
                  icon={<DownloadOutlined />}
                  onClick={handleExportCsv}
                  disabled={running || items.length === 0}
                >
                  Export CSV
                </Button>
                <Button icon={<ReloadOutlined />} onClick={handleReset}>
                  Clear
                </Button>
                {running ? (
                  <Button danger icon={<StopOutlined />} onClick={handleCancel}>
                    Cancel
                  </Button>
                ) : null}
                <Input
                  placeholder="Search copy"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  style={{ minWidth: 260 }}
                />
              </Space>

              {progress.total > 0 && (
                <Progress
                  percent={smoothPercent}
                  status={running ? "active" : "normal"}
                />
              )}
            </Space>
          </Card>
        </Col>
        <Col xs={24} lg={10}>
          <Card className="panel-card">
            <Space direction="vertical" size="middle" style={{ width: "100%" }}>
              <Text strong>Settings</Text>
              <div>
                <Text type="secondary">Tone</Text>
                <Select
                  value={copyTone}
                  onChange={setCopyTone}
                  options={[
                    { value: "Performance" },
                    { value: "Premium" },
                    { value: "Playful" },
                    { value: "Minimal" },
                    { value: "Bold" },
                  ]}
                  style={{ width: "100%" }}
                />
              </div>
              <div>
                <Text type="secondary">Creativity</Text>
                <Slider
                  min={0}
                  max={1}
                  step={0.1}
                  value={creativity}
                  onChange={setCreativity}
                />
              </div>
              <div>
                <Text type="secondary">Quality controls</Text>
                <Space wrap>
                  <Button
                    type={enforceLimits ? "primary" : "default"}
                    size="small"
                    onClick={() => setEnforceLimits((prev) => !prev)}
                  >
                    {enforceLimits ? "Limits On" : "Limits Off"}
                  </Button>
                  <Button
                    type={dedupe ? "primary" : "default"}
                    size="small"
                    onClick={() => setDedupe((prev) => !prev)}
                  >
                    {dedupe ? "Dedup On" : "Dedup Off"}
                  </Button>
                </Space>
              </div>
              <div>
                <Text type="secondary">Layout</Text>
                <Segmented
                  value={gridColumns}
                  onChange={setGridColumns}
                  options={[
                    { value: 2, label: "2 Col" },
                    { value: 3, label: "3 Col" },
                    { value: 4, label: "4 Col" },
                  ]}
                />
              </div>
            </Space>
          </Card>
        </Col>
      </Row>

      <div
        className="post-grid"
        style={{ "--post-columns": gridColumns }}
      >
        {filteredItems.length === 0 && fileList.length > 0 ? (
          <Alert type="info" message="No matches for this search." />
        ) : null}
        {filteredItems.map((item) => (
          <Card
            key={item.id}
            className="panel-card post-card"
            title={item.fileName}
            extra={
              item.status === "error" ? (
                <Text type="danger">{item.error || "Failed"}</Text>
              ) : item.status === "analyzing" ? (
                <Space size="small">
                  <Spin size="small" />
                  <Text type="secondary">Analyzing</Text>
                </Space>
              ) : (
                <Tag color={item.status === "done" ? "green" : "default"}>
                  {item.status}
                </Tag>
              )
            }
          >
            <Spin spinning={item.status === "analyzing"}>
              <Row gutter={[16, 16]}>
              <Col xs={24} md={8}>
                {item.previewUrl ? (
                  String(item.file?.type || "").startsWith("video") ? (
                    <video
                      src={item.previewUrl}
                      controls
                      className="post-preview"
                    />
                  ) : (
                    <img
                      src={item.previewUrl}
                      alt={item.fileName}
                      className="post-preview"
                    />
                  )
                ) : null}
                {item.status === "done" ? (
                  <Space size="small" style={{ marginTop: 8 }}>
                    <Tooltip title="Like this output">
                      <Button
                        size="small"
                        shape="circle"
                        icon={
                          likedIds.has(buildPostPreferenceId(item.id, promptChannels[0], item.postType?.format))
                            ? <LikeFilled />
                            : <LikeOutlined />
                        }
                        onClick={() => savePostPreference(item, promptChannels[0], "like")}
                      />
                    </Tooltip>
                    <Tooltip title="Dislike this output">
                      <Button
                        size="small"
                        shape="circle"
                        icon={
                          dislikedIds.has(buildPostPreferenceId(item.id, promptChannels[0], item.postType?.format))
                            ? <DislikeFilled />
                            : <DislikeOutlined />
                        }
                        onClick={() => savePostPreference(item, promptChannels[0], "dislike")}
                      />
                    </Tooltip>
                  </Space>
                ) : null}
              </Col>
              <Col xs={24} md={16}>
                <Tabs
                  size="small"
                  items={[
                    {
                      key: "copy",
                      label: <Space size={4}><CopyOutlined />Copy</Space>,
                      children: (
                        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                          {renderCopyContent(
                            item.copyByChannel?.[promptChannels[0]] || {},
                            item.postType?.format,
                            `${item.id}-copy`,
                          )}
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            Click any block to copy.
                          </Text>
                        </Space>
                      ),
                    },
                    {
                      key: "analysis",
                      label: <Space size={4}><EyeOutlined />Analysis</Space>,
                      children: item.analysis ? (
                        <AnalysisView analysis={item.analysis} />
                      ) : (
                        <Text type="secondary">No analysis available.</Text>
                      ),
                    },
                    {
                      key: "prompt",
                      label: <Space size={4}><FileTextOutlined />Prompt</Space>,
                      children: item.promptUsed ? (
                        <PromptView prompt={item.promptUsed} />
                      ) : (
                        <Text type="secondary">
                          {item.status === "done"
                            ? "Prompt not captured (generated before this feature was added)."
                            : "Prompt will appear here after generation."}
                        </Text>
                      ),
                    },
                  ]}
                />
              </Col>
              </Row>
            </Spin>
          </Card>
        ))}
      </div>

      <PromptEditorModal
        open={promptEditorOpen}
        onClose={() => setPromptEditorOpen(false)}
        title="Ad Copy Prompts — Post Page"
        prompts={[{
          id: "main",
          label: "Ad Copy Generation Prompt",
          value: lastPromptPreview || "(Run an analysis first to preview the live prompt, or type a custom prompt override below.)",
          description: "The full prompt sent to the model to generate headlines and primary texts. Includes analysis JSON, copy guidance, brand context, and format rules.",
        }]}
        overrides={promptOverrides}
        onSave={(saved) => setPromptOverrides(saved)}
      />
    </Space>
  );
};

export default PostPage;
